// @ts-expect-error virtual file
import { useRequestEvent, useRequestFetch, useState } from '#imports'
import { defu } from 'defu'
import { appendResponseHeader } from 'h3'
import { computed } from 'vue'

export interface UserSession {
  accessToken?: {
    token: string
    expiresAt: string | number
  } | null
  user?: any
}

export function useUserSession() {
  const serverEvent = import.meta.server ? useRequestEvent() : null
  const sessionState = useState<UserSession>('_userSession', () => ({}))
  const authReadyState = useState('_userAuthReady', () => false)

  async function logout() {
    await useRequestFetch()('/api/_auth/logout', {
      method: 'DELETE',
      onResponse({ response: { headers } }) {
        if (import.meta.server && serverEvent) {
          for (const setCookie of headers.getSetCookie()) {
            appendResponseHeader(serverEvent, 'Set-Cookie', setCookie)
          }
        }
      },
    })
    sessionState.value = {
      ...sessionState.value,
      accessToken: null,
    }
  }

  async function fetch() {
    sessionState.value = await useRequestFetch()('/api/_auth/session', {
      headers: {
        accept: 'application/json',
      },
      retry: false,
    }).catch(() => ({}))
    if (!authReadyState.value) {
      authReadyState.value = true
    }
  }

  async function login(accessToken: string) {
    sessionState.value = await useRequestFetch()('/api/_auth/login', {
      method: 'POST',
      body: {
        accessToken,
      },
      onResponse({ response: { headers } }) {
        if (import.meta.server && serverEvent) {
          for (const setCookie of headers.getSetCookie()) {
            appendResponseHeader(serverEvent, 'Set-Cookie', setCookie)
          }
        }
      },
    }).catch(() => ({}))
    if (!authReadyState.value) {
      authReadyState.value = true
    }
  }

  async function refreshToken() {
    const response = await useRequestFetch()('/api/_auth/refreshToken', {
      method: 'POST',
      onResponse({ response: { headers } }) {
        if (import.meta.server && serverEvent) {
          for (const setCookie of headers.getSetCookie()) {
            appendResponseHeader(serverEvent, 'Set-Cookie', setCookie)
          }
        }
      },
    }).catch(() => ({}))
    sessionState.value = defu(response, sessionState.value)
  }

  return {
    logout,
    fetch,
    refreshToken,
    login,
    isReady: computed(() => authReadyState.value),
    accessToken: computed(() => sessionState.value?.accessToken),
    isLoggedIn: computed(() => !!sessionState.value?.accessToken),
    user: computed(() => sessionState.value?.user),
  }
}
